import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AddToCartActionData, CartQueueItem, CartService } from '@libs/shared/services';
import { CartTrackingService, ProductTrackingListDirective } from '@libs/shared/tracking';
import { CartItemModel, CartModel, CatalogSearchSuggestionModel, ProductModel } from '@libs/shop/models';
import { AddToCartSmallElementComponent } from './add-to-cart-small-element';

@Component({
  selector: 'ui-add-to-cart-small',
  imports: [
    AddToCartSmallElementComponent,
  ],
  templateUrl: './add-to-cart-small.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddToCartSmallComponent {
  public sku: InputSignal<string> = input.required();
  public cartQueue: Signal<CartQueueItem[]> = toSignal(this.cartService.getCartQueue(), { initialValue: [] });
  public isAddToCartLoading: Signal<boolean> = computed(() => this.cartQueue()
    .findIndex((item) => (item.data as AddToCartActionData).sku === this.sku()) > -1);

  public product: InputSignal<CatalogSearchSuggestionModel | ProductModel> = input.required();
  public position: InputSignal<number> = input.required();

  public productInCartCount: Signal<number> = computed(() => this.cartItem()?.quantity ?? 0);

  private readonly cart: Signal<CartModel | undefined> = toSignal(this.cartService.getCart());
  private readonly cartItem: Signal<CartItemModel | undefined> = computed(() => this.cart()?.items.find(x => x.sku === this.sku()));

  private readonly cartTrackingService: CartTrackingService = inject(CartTrackingService);
  private readonly productTrackingList: ProductTrackingListDirective | null = inject(ProductTrackingListDirective, { optional: true });

  public constructor(
    private readonly cartService: CartService,
  ) {
  }

  public onProductQuantityChange(quantity: number): void {
    const cartItem: CartItemModel | undefined = this.cartItem();

    if (!cartItem) {
      throw new Error('ChangeQuantityInCart: Cart item is undefined');
    }

    void this.cartService.queueCartItem('changeQuantity', {
      sku: this.sku(),
      cartItem: {
        ...cartItem,
        quantity: quantity,
      },
    });

    this.cartTrackingService.trackCartQuantityChange(quantity, cartItem, this.productTrackingList?.trackingList());
  }

  public onProductRemoveFromCart(): void {
    const cartItem: CartItemModel | undefined = this.cartItem();

    if (!cartItem) {
      throw new Error('removeProductFromCart: Cart item is undefined');
    }

    void this.cartService.queueCartItem('remove', {
      sku: this.sku(),
      cartItem: cartItem,
    });

    this.cartTrackingService.trackRemoveFromCartProducts([
      {
        ...cartItem.productData,
        type: 'product',
        position: this.position(),
        quantity: cartItem.quantity,
      },
    ], this.productTrackingList?.trackingList());
  }

  public onAddToCart(): void {
    void this.cartService.queueCartItem('add', {
      sku: this.sku(),
      quantity: 1,
    });

    const productItem: CatalogSearchSuggestionModel | ProductModel = this.product();

    if ('isFree' in productItem) {
      this.cartTrackingService.trackAddToCartProducts([
          {
            ...productItem,
            type: 'product',
            position: this.position(),
            quantity: 1,
          },
        ],
        this.productTrackingList?.trackingList());
    } else {
      this.cartTrackingService.trackAddToCartProducts([
          {
            ...productItem,
            type: 'suggestion',
            position: this.position(),
            quantity: 1,
          },
        ],
        this.productTrackingList?.trackingList());
    }
  }
}
